import { Search, Handshake, Scale, Wallet, Coins, Wrench } from "lucide-react";

const STATS = [
  { value: "100%", label: "Ownership", caption: "Your name on the title, alone" },
  { value: "1", label: "Sole owner", caption: "No co-owners to coordinate with" },
  { value: "All 12", label: "Months a year", caption: "Stay, rent, or leave it — your call" },
];

const SERVICES = [
  {
    icon: Search,
    title: "Property sourcing",
    body: "We hunt off-market and vetted on-market apartments matching your brief in Tel Aviv, Jerusalem, Herzliya and Netanya.",
  },
  {
    icon: Handshake,
    title: "Price negotiation",
    body: "Local, honest negotiation with sellers and agents on your behalf — you never enter the room alone.",
  },
  {
    icon: Scale,
    title: "Legal closing",
    body: "Trusted Israeli counsel handles due diligence, contracts, tabu (land registry) and closing — end to end.",
  },
  {
    icon: Wallet,
    title: "Mortgage & finance",
    body: "Introductions to reputable Israeli mortgage brokers if you'd like financing on part of the purchase.",
  },
  {
    icon: Coins,
    title: "Currency exchange",
    body: "Vetted FX partners so your funds move efficiently and safely from home currency to shekels.",
  },
  {
    icon: Wrench,
    title: "Ongoing management",
    body: "Once you own it, we run it — tenants (if you'd like them), maintenance, taxes, cleaning, changeovers, everything.",
  },
];

export default function OwnOutright() {
  return (
    <section
      id="own-outright"
      data-testid="own-outright-section"
      className="relative bg-[var(--ink)] text-[var(--bone)] overflow-hidden"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="container mx-auto px-6 md:px-12 lg:px-16 max-w-[1400px] py-24 md:py-36 lg:py-44 relative">
        {/* Heading */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7 reveal">
            <div className="text-overline text-[var(--gold)] mb-6 flex items-center gap-4">
              <span className="h-px w-10 bg-[var(--gold)]/60" />
              <span>The third path</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[3.8rem] leading-[1.05] tracking-tight">
              Prefer to own it all?
              <br />
              <span className="italic text-[var(--bone)]/90">
                We&rsquo;ll find your ideal apartment, and manage the process — <span className="text-[var(--gold)] not-italic font-medium">end to end.</span>
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5 reveal">
            <p className="text-[var(--bone)]/75 text-base md:text-lg leading-relaxed font-light max-w-md">
              Some families don&rsquo;t want a share. They want the whole apartment.
              Tell us exactly what you&rsquo;re looking for and we handle every step —
              from finding it, to closing it, to running it while you live your life abroad.
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--bone)]/10 border border-[var(--bone)]/15">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`ownoutright-stat-${i}`}
              className="bg-[var(--ink)] p-10 md:p-14 reveal"
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <div className="font-serif-display text-7xl md:text-8xl lg:text-[7.5rem] leading-none tracking-tighter text-[var(--bone)]">
                {s.value}
              </div>
              <div className="mt-4 text-overline text-[var(--gold)]">{s.label}</div>
              <div className="mt-3 text-[var(--bone)]/70 text-base font-light">{s.caption}</div>
            </div>
          ))}
        </div>

        {/* Services grid */}
        <div className="mt-16 md:mt-24">
          <div className="text-overline text-[var(--gold)] mb-8 md:mb-12 reveal">
            Everything we handle for you
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--bone)]/10 border border-[var(--bone)]/15">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                data-testid={`ownoutright-service-${i}`}
                className="bg-[var(--ink)] p-8 md:p-10 flex flex-col gap-5 reveal"
                style={{ transitionDelay: `${(i % 3) * 0.07}s` }}
              >
                <s.icon className="h-6 w-6 text-[var(--gold)]" strokeWidth={1.6} />
                <h3 className="font-serif-display text-2xl md:text-[1.55rem] leading-tight tracking-tight text-[var(--bone)]">
                  {s.title}
                </h3>
                <p className="text-[var(--bone)]/70 text-sm md:text-base leading-relaxed font-light">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Three-way snapshot */}
        <div className="mt-16 md:mt-24 border border-[var(--bone)]/15 bg-[var(--ink)] reveal">
          <div className="grid grid-cols-1 md:grid-cols-3 md:divide-x divide-[var(--bone)]/15 divide-y md:divide-y-0">
            <div className="p-8 md:p-10">
              <div className="text-overline text-[var(--gold)] mb-3">Co-Own</div>
              <h4 className="font-serif-display text-2xl md:text-[1.55rem] tracking-tight leading-tight text-[var(--bone)]">
                You live in it.
              </h4>
              <p className="mt-3 text-[var(--bone)]/65 text-sm md:text-[0.95rem] font-light leading-relaxed max-w-sm">
                Own 25–50%, spend weeks or months each year in the apartment at your allocated time,
                and rent it out when you&rsquo;re not there.
              </p>
            </div>
            <div className="p-8 md:p-10">
              <div className="text-overline mb-3" style={{ color: "#E87A4C" }}>Co-Invest</div>
              <h4 className="font-serif-display text-2xl md:text-[1.55rem] tracking-tight leading-tight text-[var(--bone)]">
                You invest in it.
              </h4>
              <p className="mt-3 text-[var(--bone)]/65 text-sm md:text-[0.95rem] font-light leading-relaxed max-w-sm">
                Same matching, legal framework and app — with a long-term tenant in place
                and monthly distributions.
              </p>
            </div>
            <div className="p-8 md:p-10 bg-[var(--ink-hover)]">
              <div className="text-overline text-[var(--gold)] mb-3">Own outright</div>
              <h4 className="font-serif-display text-2xl md:text-[1.55rem] tracking-tight leading-tight text-[var(--bone)]">
                You own it all.
              </h4>
              <p className="mt-3 text-[var(--bone)]/70 text-sm md:text-[0.95rem] font-light leading-relaxed max-w-sm">
                Tell us your brief, we find it, negotiate it, close it and manage it — while you enjoy
                full sole ownership.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
